import { Logger } from "@suse-o11y/logger";
import { EMPTY, mergeMap, of } from "@suse-o11y/rxjs";
import produce from "immer";
import defaults from "lodash/defaults";
import omit from "lodash/omit";
import type { AnyAction } from "redux";
import type { Epic } from "redux-observable";

import { warningMessageHalfYearLimit } from "../../../../components/react/timeline/TimelineUtils";
import { combineEpicsAndCatchErrors } from "../../../../generic/redux/CombineEpicsAndCatchErrors";
import { typedStream } from "../../../../generic/redux/TypedRedux";
import { validateSync } from "../../../../generic/Util";
import {
	type TimelineQuery,
	VALIDATION_ERROR_MORE_THAN_HALF_YEAR,
	adjustTimeRangeAndTimestamp,
	timelineSchema,
} from "../../../../routes/timeline/TimelineQueryUtils";
import type { RootState } from "../../RootState";
import { mergeViewParams } from "../../route/RouteActions";

import type { TimelineUpdateRequested } from "./TimelineActions";
import { TIMELINE_UPDATE_REQUESTED } from "./TimelineActionTypes";
import { selectDefaultTimeRange, selectTimelineQueryParams } from "./TimelineSelectors";
import { adjustTimeRangeToHalfYearLimit } from "./TimeRange";

export const logger = Logger.namespace("TimelineEpic");

export const UpdateTimelineEpic: Epic<AnyAction, AnyAction, RootState> = (actions, states) =>
	actions.pipe(
		typedStream<TimelineUpdateRequested>(TIMELINE_UPDATE_REQUESTED),
		mergeMap((action) => {
			const defaultTimeRange = selectDefaultTimeRange(states.value);
			const timelineQueryParams = produce((timelineQueryParams) => {
				defaults(timelineQueryParams, { timeRange: defaultTimeRange });
			})(selectTimelineQueryParams(states.value));
			const query = adjustTimeRangeAndTimestamp(defaults(omit(action, "type"), timelineQueryParams) as TimelineQuery);
			const error = validateSync(timelineSchema, query);
			if (error != null) {
				if (error.type === VALIDATION_ERROR_MORE_THAN_HALF_YEAR) {
					warningMessageHalfYearLimit();
					const fixedTimeQuery = {
						...query,
						timeRange: adjustTimeRangeToHalfYearLimit(query.timeRange),
					};
					return of(mergeViewParams(fixedTimeQuery));
				}
				return EMPTY;
			}
			return of(mergeViewParams(query));
		}),
	);

export const TimelineEpic = combineEpicsAndCatchErrors(UpdateTimelineEpic);
